import { useState, useEffect, useMemo, type JSX } from 'react';
import { Plus, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { escapeOrIlikeTerm } from '../../lib/postgrestEscape';
import { useApp } from '../../hooks/useApp';
import { useSettingsConfig } from '../../hooks/useSettingsConfig';
import ArbiSelect from '../../components/ui/ArbiSelect';
import FulfillmentQueue from './FulfillmentQueue';
import type { Distributor } from '../../types';

// Exclude terminal statuses
const ACTIVE_STATUSES = null; // null = all, RPC returns all; filtered by non-terminal in RPC

// Fallback when fulfillment_path_config hasn't loaded yet (60s TTL cache miss).
// Day 60b — operator-input dropdown is filtered through activePaths at render
// time; this static list is the rendering fallback only.
const CHANNEL_OPTIONS_FALLBACK = [
  { value: 'amazon_fba', label: 'Amazon FBA' },
  { value: 'amazon_fbm', label: 'Amazon FBM' },
  { value: 'ebay', label: 'eBay' },
  { value: 'walmart_self', label: 'Walmart Self' },
  { value: 'walmart_wfs', label: 'Walmart WFS' },
  { value: 'shopify', label: 'Shopify' },
];

interface LogOrderForm {
  product_id: string;
  channel: string;
  marketplace_order_id: string;
  quantity: number;
  unit_price: string;
  distributor_id: string;
}

interface ProductOption {
  value: string;
  label: string;
}

interface LogOrderModalProps {
  distributors: Distributor[];
  onClose: () => void;
  onSaved: () => void;
}

function LogOrderModal({ distributors, onClose, onSaved }: LogOrderModalProps): JSX.Element {
  const { activePaths } = useSettingsConfig();
  const [form, setForm] = useState<LogOrderForm>({
    product_id: '',
    channel: '',
    marketplace_order_id: '',
    quantity: 1,
    unit_price: '',
    distributor_id: '',
  });
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');

  const channelOptions = useMemo(() => {
    if (!activePaths || activePaths.length === 0) return CHANNEL_OPTIONS_FALLBACK;
    return activePaths.map((p) => ({ value: p.path_key, label: p.display_name }));
  }, [activePaths]);

  useEffect(() => {
    if (search.length < 2) {
      setProductOptions([]);
      return;
    }
    // Day 76 P5.1 Phase 9A — escapeOrIlikeTerm adoption (Day 73 P4.1 injection-class hardening)
    const term = escapeOrIlikeTerm(search);
    supabase
      .from('products')
      .select('id, isg_sku, title')
      .or(`isg_sku.ilike.${term},title.ilike.${term}`)
      .limit(20)
      .then(({ data }) => {
        const rows = (data ?? []) as Array<{
          id: string;
          isg_sku: string | null;
          title: string | null;
        }>;
        setProductOptions(
          rows.map((p) => ({
            value: p.id,
            label: `${p.isg_sku} — ${p.title?.slice(0, 40)}`,
          }))
        );
      });
  }, [search]);

  const handleSave = async (): Promise<void> => {
    if (!form.product_id || !form.channel) return;
    setSaving(true);
    const { data: orderNum } = await supabase.rpc('next_order_number');
    const { error } = await supabase.from('fulfillment_orders').insert({
      order_number: orderNum,
      product_id: form.product_id,
      channel: form.channel,
      marketplace_order_id: form.marketplace_order_id || null,
      quantity: form.quantity || 1,
      unit_price: form.unit_price ? parseFloat(form.unit_price) : null,
      distributor_id: form.distributor_id || null,
      status: 'new_order',
    } as never);
    setSaving(false);
    if (!error) {
      onSaved();
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 'var(--z-modal, 200)' }}
    >
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        className="absolute inset-0 bg-surface-0/80 cursor-default"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Log Order"
        className="relative bg-surface-1 border border-border rounded-xl shadow-xl w-full max-w-md mx-4"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="text-sm font-semibold text-ink-bright">Log Order</h2>
          <button
            onClick={onClose}
            className="arbi-btn-ghost p-1 rounded cursor-pointer"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label
              htmlFor="log-order-search"
              className="text-xs font-medium text-ink-secondary block mb-1"
            >
              Product <span className="text-danger">*</span>
            </label>
            <input
              id="log-order-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by ISG SKU or title..."
              className="arbi-input px-3 py-1.5 text-xs w-full mb-1"
            />
            {productOptions.length > 0 && (
              <div id="log-order-product">
                <ArbiSelect
                  value={form.product_id}
                  options={productOptions}
                  onChange={(v) => setForm((f) => ({ ...f, product_id: String(v) }))}
                  placeholder="Select product..."
                />
              </div>
            )}
          </div>
          <div>
            <label
              htmlFor="log-order-channel"
              className="text-xs font-medium text-ink-secondary block mb-1"
            >
              Channel <span className="text-danger">*</span>
            </label>
            <div id="log-order-channel">
              <ArbiSelect
                value={form.channel}
                options={channelOptions}
                onChange={(v) => setForm((f) => ({ ...f, channel: String(v) }))}
                placeholder="Select channel..."
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-xs font-medium text-ink-secondary block mb-1">Quantity</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  aria-label="Decrease quantity"
                  onClick={() => setForm((f) => ({ ...f, quantity: Math.max(1, f.quantity - 1) }))}
                  className="arbi-btn-ghost px-2 py-1 text-xs cursor-pointer"
                >
                  &minus;
                </button>
                <span className="text-xs font-mono w-8 text-center">{form.quantity}</span>
                <button
                  type="button"
                  aria-label="Increase quantity"
                  onClick={() => setForm((f) => ({ ...f, quantity: f.quantity + 1 }))}
                  className="arbi-btn-ghost px-2 py-1 text-xs cursor-pointer"
                >
                  +
                </button>
              </div>
            </div>
            <div>
              <label
                htmlFor="log-order-price"
                className="text-xs font-medium text-ink-secondary block mb-1"
              >
                Unit Price
              </label>
              <input
                id="log-order-price"
                value={form.unit_price}
                onChange={(e) =>
                  setForm((f) => ({ ...f, unit_price: e.target.value.replace(/[^0-9.]/g, '') }))
                }
                className="arbi-input px-3 py-1.5 text-xs w-full font-mono"
                placeholder="0.00"
                type="text"
                inputMode="decimal"
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="log-order-mpoid"
              className="text-xs font-medium text-ink-secondary block mb-1"
            >
              Marketplace Order ID
            </label>
            <input
              id="log-order-mpoid"
              value={form.marketplace_order_id}
              onChange={(e) => setForm((f) => ({ ...f, marketplace_order_id: e.target.value }))}
              className="arbi-input px-3 py-1.5 text-xs w-full font-mono"
              placeholder="External order ID..."
            />
          </div>
          <div>
            <label
              htmlFor="log-order-distributor"
              className="text-xs font-medium text-ink-secondary block mb-1"
            >
              Distributor
            </label>
            <div id="log-order-distributor">
              <ArbiSelect
                value={form.distributor_id}
                options={distributors.map((d) => ({ value: d.id, label: d.name }))}
                onChange={(v) => setForm((f) => ({ ...f, distributor_id: String(v) }))}
                placeholder="Select distributor..."
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
          <button onClick={onClose} className="arbi-btn-ghost text-xs px-3 py-1.5 cursor-pointer">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.product_id || !form.channel}
            className="arbi-btn-primary text-xs px-3 py-1.5 cursor-pointer disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Log Order'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FulfillmentOrders(): JSX.Element {
  // Day 64.5 V-ROUI: viewer extends the existing dist_rep gate on the
  // Log Order action button + LogOrderModal mount. Per §5 matrix viewer
  // view-only on Fulfillment.
  const { distributors, isDistRep, isViewer } = useApp();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  return (
    <>
      <FulfillmentQueue
        key={refreshKey}
        title="Orders"
        statusFilter={ACTIVE_STATUSES}
        showStatusCol={true}
        emptyMessage={
          isDistRep
            ? 'No fulfillment orders yet'
            : 'No fulfillment orders yet — log an order to get started'
        }
        // Architecture Rule 4: dist_rep is read-only on Fulfillment. Hide the
        // Log Order action button + modal entirely. The unit_price input field
        // inside the modal is the specific pricing leak surface; gating the
        // affordance at the entry point is sufficient defense at the UI layer.
        // (See BACKLOG B24 for the matching RLS-layer tightening on
        // fulfillment_orders INSERT.)
        actionButton={
          isDistRep || isViewer ? undefined : (
            <button
              onClick={() => setModalOpen(true)}
              className="arbi-btn-primary text-xs px-3 py-1 flex items-center gap-1 cursor-pointer"
            >
              <Plus size={12} /> Log Order
            </button>
          )
        }
      />
      {modalOpen && !isDistRep && !isViewer && (
        <LogOrderModal
          distributors={distributors}
          onClose={() => setModalOpen(false)}
          onSaved={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </>
  );
}
